import { Link } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";

const services = [
  {
    id: "clarity-call",
    badge: "01 — Signature Offer",
    name: "Content Clarity Call",
    price: 75,
    duration: "30 minutes · Live, 1-on-1 on Zoom",
    forWhom:
      "For the founder, creator, or strategist who has the ideas — but the messaging keeps coming out muddy.",
    description:
      "Bring your scattered thoughts, your half-written captions, that one offer you keep almost-launching. In one focused half-hour, we cut through the noise and walk away with a sharper hook, a clear next step, and language that finally sounds like you.",
    deliverables: [
      "Live brand-voice diagnostic (your real voice, not a template)",
      "One sharpened hook or signature message you can publish this week",
      "A clear next step — not a 47-page workbook",
      "Replay link, so you can revisit what we found together",
    ],
    cta: "Book the Clarity Call",
    image:
      "https://images.unsplash.com/photo-1573166953836-06864dc70a21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHw0fHxibGFjayUyMHdvbWFuJTIwZW50cmVwcmVuZXVyJTIwbGFwdG9wfGVufDB8fHx8MTc3OTczNjM2OHww&ixlib=rb-4.1.0&q=85",
  },
  {
    id: "starter",
    badge: "02 — Most Popular",
    name: "30-Day Content Starter",
    price: 250,
    duration: "Done-with-you · Delivered in 7 days",
    forWhom:
      "For the woman ready to show up consistently for a full month — without spiraling every Sunday night.",
    description:
      "A full month of strategic, audience-aligned content ideas, plus a content calendar built around your real life. We use AI like the quiet assistant it was meant to be — keeping the voice yours, the strategy clear, and the workload sane.",
    deliverables: [
      "30 days of strategic content prompts mapped to your offers",
      "A content calendar setup you can actually keep up with",
      "Brand voice notes — words to use, words to retire",
      "A 30-minute kickoff call to align everything to your goals",
      "Loom walkthrough of your AI workflow (so you can repeat it)",
    ],
    cta: "Book the Starter",
    image:
      "https://images.unsplash.com/photo-1573164574001-518958d9baa2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxibGFjayUyMHdvbWFuJTIwd29ya2luZyUyMG9mZmljZSUyMHdhcm18ZW58MHx8fHwxNzc5NzM2MzY4fDA&ixlib=rb-4.1.0&q=85",
  },
];

export default function Services() {
  return (
    <div data-testid="page-services">
      {/* HEADER */}
      <section className="px-6 md:px-12 lg:px-16 pt-20 md:pt-28 pb-16 text-center">
        <p className="overline text-[var(--gold)] rise">Services & investment</p>
        <h1 className="font-serif-display mt-5 text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-[var(--indigo)] rise rise-d1">
          Two doors.{" "}
          <span className="italic text-[var(--gold)]">One outcome</span>
          <br className="hidden md:block" /> — content that sounds like you.
        </h1>
        <p className="mt-7 max-w-2xl mx-auto text-[1.05rem] leading-relaxed text-[var(--ink-muted)] rise rise-d2">
          Whether you have thirty minutes or thirty days, here's how we build
          your voice into a system you'll actually use.
        </p>
        <div className="max-w-3xl mx-auto mt-16 hairline" />
      </section>

      {/* SERVICES */}
      <section className="px-6 md:px-12 lg:px-16 pb-24 md:pb-32">
        <div className="max-w-6xl mx-auto flex flex-col gap-24 md:gap-32">
          {services.map((s, idx) => (
            <article
              key={s.id}
              className={`grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center ${
                idx % 2 === 1 ? "md:[&>div:first-child]:order-2" : ""
              }`}
              data-testid={`service-${s.id}`}
            >
              {/* Image */}
              <div className="md:col-span-5 relative">
                <div className="absolute -top-4 -left-4 w-full h-full border border-[var(--gold)]/50" aria-hidden />
                <img
                  src={s.image}
                  alt={s.name}
                  className="relative w-full h-[420px] md:h-[520px] object-cover"
                  data-testid={`service-image-${s.id}`}
                />
              </div>

              {/* Content */}
              <div className="md:col-span-7">
                <p className="overline text-[var(--gold)]">{s.badge}</p>
                <h2 className="font-serif-display text-4xl md:text-5xl lg:text-[3.4rem] leading-[1.08] tracking-tight text-[var(--indigo)] mt-4">
                  {s.name}
                </h2>

                <div className="flex flex-wrap items-baseline gap-4 mt-6">
                  <span className="font-serif-display text-4xl md:text-5xl text-[var(--indigo)]" data-testid={`service-price-${s.id}`}>
                    ${s.price}
                  </span>
                  <span className="text-sm text-[var(--ink-muted)] tracking-wide">
                    {s.duration}
                  </span>
                </div>

                <p className="mt-7 text-[1.05rem] leading-relaxed text-[var(--ink-muted)] italic font-serif-display">
                  {s.forWhom}
                </p>
                <p className="mt-5 text-[1.02rem] leading-relaxed text-[var(--ink-muted)]">
                  {s.description}
                </p>

                <div className="mt-8">
                  <p className="overline text-[var(--indigo)] mb-4">What's included</p>
                  <ul className="space-y-3">
                    {s.deliverables.map((d) => (
                      <li
                        key={d}
                        className="flex items-start gap-3 text-[0.98rem] text-[var(--ink-muted)] leading-relaxed"
                      >
                        <Check
                          size={18}
                          className="text-[var(--gold)] mt-1 flex-shrink-0"
                        />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link
                  to="/book"
                  className="btn-gold mt-10 inline-flex group"
                  data-testid={`service-cta-${s.id}`}
                >
                  {s.cta}
                  <ArrowRight
                    size={16}
                    className="transition-transform duration-300 group-hover:translate-x-1"
                  />
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* COMPARE BLOCK */}
      <section
        className="bg-[var(--indigo)] text-[var(--cream)] px-6 md:px-12 lg:px-16 py-24 md:py-32 grain relative"
        data-testid="services-compare"
      >
        <div className="max-w-5xl mx-auto text-center">
          <p className="overline text-[var(--gold)]">Not sure which one?</p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.08] tracking-tight mt-5">
            Start with the call. <br className="hidden md:block" />
            <span className="italic text-[var(--gold)]">We'll figure out the rest together.</span>
          </h2>
          <p className="mt-7 max-w-2xl mx-auto text-[var(--cream)]/75 text-[1.05rem] leading-relaxed">
            Most clients begin with the Clarity Call. If the Starter is the right
            next move, we'll know in thirty minutes — and your $75 gets credited
            forward.
          </p>
          <Link to="/book" className="btn-gold mt-10 inline-flex" data-testid="services-cta-final">
            Book Your Session →
          </Link>
        </div>
      </section>
    </div>
  );
}
